import pako from 'pako';
import pick from './pick.js';
import queryString from './queryString.js';
import sleep from './sleep.js';
import store from './store.js';
import uuid from './uuid.js'

/**
 * Content types that are considered as a text.
 * Responses with these content types have their body content logged into console and thrown error.
 * @type {Array<string>}
 */
const textContentTypes = ['application/json', 'text/plain'];

/**
 * Response HTTP statuses on which an another fetch attempt is allowed.
 * @type {Array<number>}
 */
const retryStatuses = [429, 502, 503, 504];

const EXPONENTIAL_BACKOFF_RETRIES = 3;

export const msDelay = (attempt, useLinearBackoff) => {
  if (useLinearBackoff) {
    return parseInt(500 + Math.random() * 500, 10);
  }
  return parseInt(2 ** attempt * 1000 + Math.random() * 1000, 10);
};

/**
 * Copy the error into new plain error with custom properties.
 * This is done, because we need to add the custom `code` property.
 * That isn't possible for DOMException where the `code` is read-only property.
 * @param {Error|DOMException} error
 * @param {object} props
 * @returns {Error}
 */
const copyError = (error, props) =>
  Object.assign(new Error(error.message), pick(error, 'name', 'stack'), props);

const transformError = (error, code = 500, body = '') => {
  // Only attempt JSON parsing if body looks like JSON
  if (body && typeof body === 'string') {
    const trimmedBody = body.trim();
    if (trimmedBody.startsWith('{') || trimmedBody.startsWith('[')) {
      try {
        const json = JSON.parse(body);
        if (json.data && json.data.error) {
          const message =
            typeof json.data.error === 'object' ? json.data.error.message : json.data.error;
          return copyError(error, { code, body: message });
        }
      } catch (e) {
        console.error('Failed to parse error response', { body, error: e });
      }
    }
  }
  return copyError(error, { code, body });
};

const requestHeaders = ({ gzip = false }) => {
  const headers = {};
  if (gzip) {
    headers['Content-Encoding'] = 'gzip';
    headers['Content-Type'] = 'application/octet-stream';
  }
  return headers;
};

/**
 * @param {string} url
 * @param {object} [opts]
 * @param {object} [opts.body]
 * @param {string} [opts.contentType] json | urlencoded
 * @param {number} [opts.currentAttempt]
 * @param {boolean} [opts.retryInAnyCase]
 * @param {object} [opts.headers]
 * @param {number} [opts.maximumAttempts] how many times could retry this request
 * @param {boolean} [opts.useLinearBackoff] how many times could retry this request
 * @param {string} [opts.method]
 * @param {string} [opts.mode]
 * @param {AbortSignal} [opts.signal]
 * @param {boolean} [opts.logError]
 * @param {boolean} [opts.logBodyDuringIssue]
 * @param {boolean} [opts.gzip]
 * @returns {Response}
 */
const httpRequest = async (
  url,
  {
    body,
    contentType = 'json',
    currentAttempt = 0,
    retryInAnyCase = false,
    headers,
    maximumAttempts = EXPONENTIAL_BACKOFF_RETRIES,
    useLinearBackoff = false,
    method,
    mode,
    signal,
    logError = true,
    logBodyDuringIssue = true,
    gzip = false,
  } = {},
) => {
  const retry = async () => {
    // TODO 223 Reject with AbortError if abort signal is raised during the sleep.
    await sleep(msDelay(currentAttempt, useLinearBackoff));

    if (signal?.aborted) {
      throw new Error('AbortError');
    }

    return httpRequest(url, {
      body,
      contentType,
      currentAttempt: currentAttempt + 1,
      retryInAnyCase,
      headers,
      maximumAttempts,
      useLinearBackoff,
      method,
      mode,
      signal,
      logError,
      logBodyDuringIssue,
      gzip,
    });
  };

  let formattedBody;
  try {
    if (body) {
      if (contentType === 'json') {
        formattedBody = JSON.stringify(body);
      } else if (contentType === 'urlencoded') {
        formattedBody = queryString.stringify(body);
      } else if (contentType === "multipart/form-data") {
        formattedBody = body;
      }
      if (gzip) {
        formattedBody = pako.gzip(formattedBody);
      }
    }
  } catch (error) {
    const message = error.message ?? 'Request body wrong format';
    throw transformError(error, 500, message);
  }

  let response;
  try {
    response = await fetch(url, {
      body: formattedBody,
      // Ignore the cache for repeated requests, or the same cached result can be returned.
      // TODO Allow to set an custom cache value in params.
      // - Then choose the appropriate cache method for repeated requests (no-store vs. no-cache).
      //   By the default the cache is enabled, so the repeated responses are also cached,
      //   which is done by the counter-intuitively named "no-cache" option.
      cache: currentAttempt === 0 ? undefined : 'no-cache',
      headers: {
        ...headers,
        ...requestHeaders({ gzip }),
      },
      method,
      mode,
      signal,
    });
  } catch (error) {
    const message = error.message ?? 'Unknown failed to fetch';
    if (error.name !== 'AbortError') {
      if (retryInAnyCase && currentAttempt < maximumAttempts) {
        if (logError) {
          console.log('Request in exception repeated', { method, url, error });
        }
        return retry();
      }
      if (logError) {
        console.error(message, {
          method,
          url,
          error,
          headers,
          body: logBodyDuringIssue ? body : undefined,
        });
        console.error(error.stack);
      }
    }
    throw transformError(error, 500, message);
  }

  if (!response.ok) {
    // WHEN REQUEST COULD REPEAT ITSELF
    if (currentAttempt < maximumAttempts) {
      // 429 TOO MANY REQUESTS
      // 502, 503, 504 SERVER ERRORS
      if (retryInAnyCase || retryStatuses.includes(response.status)) {
        return retry();
      }
    }

    const statusMessage = `Invalid status ${response.status}`;
    const responseContentType = response.headers.get('Content-Type');
    const isTextResponse = textContentTypes.find(item => responseContentType.indexOf(item) >= 0);
    let text = isTextResponse ? await response.text() : undefined;
    if (!text) {
      text = response.status;
    }
    const message = [statusMessage, text].filter(m => m != null).join(': ');
    const error = new Error(message);
    if (logError) {
      console.error(message, { method, url, headers, body: logBodyDuringIssue ? body : undefined });
    }
    throw transformError(error, response.status, text);
  }

  return response;
};

export const getTraceId = () => {
  const traceId = store.get("traceId");
  if (traceId) {
    return traceId;
  }

  const newTraceId = uuid();
  store.set("traceId", newTraceId);
  return newTraceId;
}

export default httpRequest;
